import * as React from "react"
import { connect } from "react-redux"
import trans, { isTrans } from "trans"
import FileInputComponent from 'react-file-input-previews-base64'

import {
    GridCell,
    TextInput,
    FieldLabel,
    Icon,
    Theme,
    Grid,
    Button,
    css,
    Spinner,
    Progress,
    Checkbox,
    Select,
    DateRange,
} from "@bdfx/mergado-ui-kit"

import {
    updateInputValue as change,
} from "app/reducers/form";

import { CustomGroupsOption, isMultiSelectCloseOnChange } from "./filters";
import { ReduxList } from "app/types/app-types";
import { AITemplatType, AvailableOptionsType, DiagnosticsType, FindSimilarType, GroupCountsType, RENDERED_TYPE_ANIM_GIF, RENDERED_TYPE_AUTOWMREMOVE, RENDERED_TYPE_BGREMOVE, RENDERED_TYPE_INPAINTING, RENDERED_TYPE_MANUAL_BGREMOVE_EDITED, RENDERED_TYPE_MANUAL_WMREMOVE, RENDERED_TYPE_OUTPAINTING, RENDERED_TYPE_UPSCALE } from "../types";
import { FiltersFormType } from "../types/filtertypes";
import { Template as TemplateType } from "app/types/model-types";
import { DarkGrayColor } from "app/helpers/colors";
import { URL } from "app/config/api-config";
import { getGroupCounts, isGodModeOn, projectId, visualSimilarityFullExportFindSimilar } from "app/helpers/api";
import { AxiosResponse } from 'axios';
import { selectFormValues } from "app/reducers/form";
import { allowSuperadminForUsers } from "template-edit/components/helper";
import { getTemplateTypeColor, getTemplateTypeIcon, getTemplateTypeTranslated } from "template-list/template-badges";
import { FileInputType } from "template-list/containers/template-list"
import { initialFindSimilarState, setFindSimilar } from "products/reducer"
import { iconDisabledColor } from "template-edit/components/tools-panel"

interface State {
    moreUrls: boolean
    groupCounts: GroupCountsType[]
    inputFileKey: number
}

interface Props {
    userId: number
    findSimilar: FindSimilarType
    templates: ReduxList<TemplateType>
    products: ReduxList<DiagnosticsType>
    availableOptions: ReduxList<AvailableOptionsType>
    filtersForm: FiltersFormType
    setIsDirty: Function
    change: typeof change
    setFindSimilar: typeof setFindSimilar
}

class ProductFilters extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            moreUrls: false,
            groupCounts: null,
            inputFileKey: 0,
        }
    }

    componentDidMount(): void {
        if (!this.state.groupCounts) {
            getGroupCounts()
                .then((response: AxiosResponse<any>) => {
                    if (response.data && Array.isArray(response.data)) {
                        if (response.data.length > 0) {
                            this.setState({ groupCounts: response.data })
                        } else {
                            console.debug('the groups count is disabled for large projects')
                        }
                    }
                })
                .catch(e => console.error('cannot get groups'))
        }
    }

    uploadImage = (file: FileInputType) => {
        this.props.setFindSimilar({ ...initialFindSimilarState })
        visualSimilarityFullExportFindSimilar(null, file)
            .then((response: AxiosResponse<any>) => {
                console.debug('full export started')
            })
            .catch(e => console.error('cannot create full export', e))
    }

    resetFindSimilar = () => {
        this.props.setFindSimilar({ ...initialFindSimilarState })
        this.setState({inputFileKey: this.state.inputFileKey+1})
    }

    render() {
        const { templates, userId, findSimilar, filtersForm, availableOptions } = this.props
        const { groupCounts } = this.state

        const isAllowSpecialFields = (allowSuperadminForUsers.includes(userId) || isGodModeOn)

        const availableTemplates = (templates && templates.isLoaded && templates.data) ? templates.data.map((t: TemplateType) => {
            const text = getTemplateTypeTranslated(t.type)
            const color = getTemplateTypeColor(t.type)
            const icon = getTemplateTypeIcon(t.type, 20, t.active ? color : iconDisabledColor)

            return {
                text: <StyledTemplateOption>
                    <GridCell title={`${t.name} (${t.id})`} className={`template-name ${t.active ? '' : 'template-inactive'}`}>{t.name} {!t.active && <small>({trans('inactive').toLowerCase()})</small>}</GridCell>
                    <GridCell title={text}>{icon}</GridCell>
                </StyledTemplateOption>,
                label: `${t.name}`,
                value: `${t.id}`,
                active: t.active,
            }
        }) : null

        const renderedTypeOptions = availableOptions?.data?.available_rendered_types && availableOptions.data.available_rendered_types.length > 1 ?
            availableOptions.data.available_rendered_types.map((t: AITemplatType) => {
            const text = getTemplateTypeTranslated(t)
            const color = getTemplateTypeColor(t)
            const icon = getTemplateTypeIcon(t, 20, color)
            return {
                text: <span>{icon} {text}</span>,
                label: text,
                value: `${t}`,
            }
        }) : null

        const renderedByOptions = availableOptions?.data?.available_rendered_by && availableOptions.data.available_rendered_by.length > 1 ?
            availableOptions.data.available_rendered_by.map((r: string) => {
            return {
                text: `${r}`,
                label: `${r}`,
                value: `${r}`,
            }
            }) : null

        const checkElementsAvailabilityOptions = availableOptions?.data?.available_elements && availableOptions.data.available_elements.length > 1 ?
            availableOptions.data.available_elements.map((e: string) => {
            return {
                text: `${e}`,
                label: `${e}`,
                value: `${e}`,
            }
            }) : null

        const availableGroupsOptions = groupCounts ? groupCounts.map(g => {
            let icon;

            switch (g.group_id) {
                case 'unavailable':
                    icon = <Icon type={'exclamation'} size={16} color={Theme.red} />
                    break
                case '200_0':
                    icon = <Icon type={'check'} size={16} color={Theme.green} />
                    break
                case '200_1':
                    icon = <Icon type={'square-o'} size={16} color={DarkGrayColor} />
                    break
                case '200_420':
                    icon = <Icon type={'question'} size={16} color={Theme.orange} />
                    break
                case '200_435':
                    icon = <Icon type={'exclamation'} size={16} color={Theme.blue} />
                    break
                case '200_465':
                    icon = <Icon type={'arrows-h'} size={16} color={Theme.red} />
                    break
                case '500_0':
                    icon = <Icon type={'exclamation-circle'} size={16} color={Theme.red} />
                    break
                case 'same_images':
                    icon = <Icon type={'clone'} size={16} color={DarkGrayColor} />
                    break
                case 'empty_images':
                    icon = <Icon type={'eye-slash'} size={16} color={DarkGrayColor} />
                    break
                case 'poor_quality_images':
                    icon = <Icon type={'frown-o'} size={16} color={DarkGrayColor} />
                    break
                case 'unsupported_mime_type':
                    icon = <Icon type={'error-light'} size={16} color={DarkGrayColor} />
                    break
                case 'not_recommended_colorspace':
                    icon = <Icon type={'tint'} size={16} color={Theme.steelblue} />
                    break
                case 'unsupported_background':
                    icon = <Icon type={'square'} size={16} color={DarkGrayColor} />
                    break
                case 'too_many_alts':
                    icon = <Icon type={'error-light'} size={16} color={DarkGrayColor} />
                    break
                case 'has_watermark':
                    icon = <Icon type={'paw'} size={16} color={DarkGrayColor} />
                    break
                case 'transparent_background':
                    icon = <Icon type={'square'} size={16} color={DarkGrayColor} />
                    break
                case 'wrong_placement':
                    icon = <Icon type={'cards-light'} size={16} color={DarkGrayColor} />
                    break
                case 'has_person':
                    icon = <Icon type={'person-radix'} size={16} color={DarkGrayColor} />
                    break
                case 'low_quality':
                    icon = <Icon type={'expand'} size={16} color={DarkGrayColor} />
                    break
                case 'invalid_url':
                    icon = <Icon type={'exclamation-triangle'} size={16} color={Theme.orange} />
                    break
                default:
                    icon = <span />
                    break
            }

            const labelTransString = `status_${g.group_id}`
            const label = isTrans(labelTransString) ? trans(labelTransString) : `${g.group_id}`

            return {
                label: label,
                title: label,
                value: `${g.group_id}`,
                icon: icon
            }
        }) : null

        const onErrorFunction = (event) => {
            if (event.currentTarget.parentNode) {
                event.currentTarget.parentNode.removeChild(event.currentTarget)
            }
        }

        return (
            <>
                <GridCell className={filtersForm.product.fulltext && filtersForm.product.fulltext.length > 0 ? 'active-filter' : ''}>
                    <TextInput
                        label={<Icon type={'magnifying-glass-radix'} text={trans('fulltext')} />}
                        name={`filtersForm.product.fulltext`}
                        id={`fulltext`}
                        type="search"
                        placeholder={trans('fulltext')}
                        onChange={() => this.props.setIsDirty(true)}
                    />
                </GridCell>

                <GridCell className={filtersForm.image?.showGroup && Array.isArray(filtersForm.image.showGroup) && filtersForm.image.showGroup.length > 0 ? "active-filter" : ''}>
                    <FieldLabel><Icon type={'dots-vertical-radix'} text={trans('filter_group')} /></FieldLabel>
                    {availableGroupsOptions &&
                        <Select
                            name={`filtersForm.image.showGroup`}
                            multiple={true}
                            closeOnChange={isMultiSelectCloseOnChange}
                            options={availableGroupsOptions.filter(g => g.value !== 'all')}
                            OptionComponent={CustomGroupsOption}
                            closeOnBlur={true}
                            onChange={(newValue) => {
                                const updatedValues = newValue.filter(g => g !== 'all')
                                this.props.change('filtersForm', 'image.showGroup', updatedValues);
                                this.props.setIsDirty(true)
                            }}
                        />
                    }
                </GridCell>

                {/* some users only */}
                {isAllowSpecialFields &&
                    <React.Fragment>
                        <GridCell className={'some-users-only'}>
                            <Checkbox
                                name={`filtersForm.product.has_alt_images`}
                                onValue={1}
                                offValue={0}
                                label={<Icon type={'grid-light'} text={trans('has_alt_images')} />}
                                onChange={() => this.props.setIsDirty(true)}
                            />
                        </GridCell>

                        <GridCell className={'some-users-only'}>
                            <Checkbox
                                name={`filtersForm.product.should_rerender`}
                                onValue={1}
                                offValue={0}
                                label={<Icon type={'circle-o-notch'} text={trans('should_rerender')} />}
                                onChange={() => this.props.setIsDirty(true)}
                            />
                        </GridCell>

                        {renderedTypeOptions &&
                            <GridCell className={'some-users-only'}>
                                <Select
                                    name={`filtersForm.product.rendered_type`}
                                    multiple={false}
                                    label={<Icon type="camera-radix" text={'rendered type'}/>}
                                    closeOnChange={isMultiSelectCloseOnChange}
                                    options={renderedTypeOptions}
                                    /* onClear={() => this.props.change('filtersForm', 'product.rendered_type', null)} */
                                    onChange={() => this.props.setIsDirty(true)}
                                />
                            </GridCell>
                        }

                        {renderedByOptions &&
                            <GridCell className={'some-users-only'}>
                                <Select
                                    name={`filtersForm.product.rendered_by`}
                                    multiple={false}
                                    label={<Icon type="box-light" text={'rendered by'}/>}
                                    closeOnChange={isMultiSelectCloseOnChange}
                                    options={renderedByOptions}
                                    /* onClear={() => this.props.change('filtersForm', 'product.rendered_by', null)} */
                                    onChange={() => this.props.setIsDirty(true)}
                                />
                            </GridCell>
                        }

                        {checkElementsAvailabilityOptions &&
                            <GridCell className={'some-users-only'}>
                                <Select
                                    name={`filtersForm.product.check_elements_availability`}
                                    multiple={true}
                                    label={<Icon type="code-radix" text={'check elements availability'}/>}
                                    closeOnChange={isMultiSelectCloseOnChange}
                                    options={checkElementsAvailabilityOptions}
                                    onChange={() => this.props.setIsDirty(true)}
                                />
                            </GridCell>
                        }

                        <GridCell className={'some-users-only'}>
                            <DateRange
                                labelFrom="render from"
                                labelTo="render to"
                                nameFrom="filtersForm.product.rendered_at.from"
                                nameTo="filtersForm.product.rendered_at.to"
                            />
                        </GridCell>
                    </React.Fragment>
                }

                <GridCell className={filtersForm.product.id ? 'active-filter' : ''}>
                    <FieldLabel><Icon type="mergado" text={trans('filter_productid')} /></FieldLabel>
                    <TextInput
                        name={`filtersForm.product.id`}
                        id={`filter_productid`}
                        type="search"
                        placeholder={trans('filter_productid')}
                        onChange={() => this.props.setIsDirty(true)}
                    />
                </GridCell>

                {availableTemplates &&
                    <GridCell className={filtersForm.product.template_id && Array.isArray(filtersForm.product.template_id) && filtersForm.product.template_id.length > 0 ? "active-filter" : ''}>
                        <Select
                            name={`filtersForm.product.template_id`}
                            multiple={true}
                            closeOnChange={isMultiSelectCloseOnChange}
                            options={availableTemplates}
                            label={<Icon type={'feedimageeditor'} text={trans('filter_template')} />}
                            onChange={() => this.props.setIsDirty(true)}
                        />
                    </GridCell>
                }

                <GridCell style={{position: 'relative'}}>
                    <span className='remove clickable' onClick={() => this.resetFindSimilar()} style={{position: 'absolute', top: '0', right: '10px'}}>
                            <Icon type={'close-light'} color={DarkGrayColor}/>
                    </span>

                    <FieldLabel><Icon type={'magnifying-glass-radix'} text={trans('find_similar_images')} /></FieldLabel>
                    <StyledFindSimilarDiv>
                        {findSimilar.isSearching && <Spinner text={trans('searching_in_progress')} />}

                        {(findSimilar.isExporting && findSimilar.exportedRows && findSimilar.totalRows) &&
                            <Progress
                                width={'100%'}
                                height={'16px'}
                                showLabel={true}
                                value={Math.round(100 * findSimilar.exportedRows / findSimilar.totalRows)}
                                color={Theme.blue}
                                max={100}
                                getValueLabel={(val) => `${val} %`}
                            />
                        }

                        {(findSimilar.similarProducts && Object.keys(findSimilar.similarProducts).length > 0) &&
                            <div className="center">
                                {Object.keys(findSimilar.similarProducts).map((h, k) => {
                                    const imageSrc = `${URL.laravelBase}get-source-image/${projectId}/${h}`
                                    return <img key={`similar_${h}_${k}`} src={imageSrc} onError={onErrorFunction}/>
                                })}
                            </div>
                        }

                        <FileInputComponent
                            id={'find-similar-upload'}
                            key={`input_file_${this.state.inputFileKey}`}
                            parentStyle={{ marginTop: 0, display: "grid" }}
                            imageContainerStyle={{textAlign: 'center'}}
                            labelStyle={{ fontSize: 14 }}
                            labelText={''}
                            multiple={false}
                            imagePreview={true}
                            buttonComponent={(findSimilar.isSearching || findSimilar.isExporting) ? <span></span> :
                                <Button
                                    id={'upload_to_find_similar'}
                                    type="void"
                                    size={'small'}
                                    color={Theme.blue}
                                    secondary={true}
                                    icon={<Icon type={'upload-light'} text={trans('upload_image')} />}
                                />
                            }
                            callbackFunction={this.uploadImage}
                            accept="image/*"
                        />
                    </StyledFindSimilarDiv>
                </GridCell>
            </>
        )
    }
}

const StyledFindSimilarDiv = css('div')({
    ' img': {
        maxWidth: '80px',
        maxHeight: '60px'
    }
})

const StyledTemplateOption = css(Grid)({
    gridTemplateColumns: 'auto 24px',
    width: '100%',
    ' .template-inactive': {
        fontStyle: 'italic',
        color: iconDisabledColor,
    }
})

function mapStateToProps(state) {
    return {
        findSimilar: state.findSimilar,
        availableOptions: state.availableOptions,
        filtersForm: selectFormValues(state, 'filtersForm'),
        templates: state.templates,
        products: state.products,
    }
}

export default connect(mapStateToProps, {
    change,
    setFindSimilar,
})(ProductFilters)
